export const TEMPLATES = [
  {
    id: "baseline",
    category: "se",
    name: "🛡️ Standard Baseline",
    description: "Production-ready POC configuration. Compatible with all Cloudflare plans (Free/Pro/Business/Enterprise).",
    benefits: [
      "✅ Full SSL/TLS encryption (TLS 1.2+ & TLS 1.3)",
      "✅ WAF protection (Cloudflare Managed Rules + OWASP Core Ruleset)",
      "✅ Modern protocols (HTTP/3 with QUIC, HTTP/2, 0-RTT, WebSockets)",
      "✅ Performance optimizations (Brotli compression, Early Hints)",
      "✅ Bot protection (Browser Integrity Check, Medium Security Level)",
      "✅ High availability (Always Online mode, IPv6 support)",
      "✅ Compatible with all plan types including Free tier"
    ],
    rules: {
      // === SECURITY (Production-grade) ===
      ssl: "full",                        // Full SSL - origin must have valid cert (use "flexible" if origin is HTTP-only)
      min_tls_version: "1.2",            // TLS 1.2+ minimum (industry standard, PCI DSS compliant)
      tls_1_3: "on",                     // Enable TLS 1.3 (30% faster handshake, more secure)
      tls_client_auth: "off",            // mTLS disabled (enable for zero-trust architectures)
      always_use_https: "on",            // Force HTTPS redirects (301) - essential for security
      automatic_https_rewrites: "on",    // Fix mixed content (http:// → https://) automatically
      
      // WAF & Threat Protection
      security_level: "medium",          // Balanced security (medium = 14+ threat score blocked)
      browser_check: "on",               // Challenge suspicious requests (blocks most bots)
      waf_managed_cloudflare: "on",      // Cloudflare Managed Ruleset (constantly updated threat intel)
      waf_managed_owasp: "on",           // OWASP Core Ruleset (protects against Top 10 vulnerabilities)
      
      // Custom WAF Rule (DISABLED by default - enable for geo-blocking or custom logic)
      waf_enable: "off",                 // Set to "on" to enable custom rule below
      waf_name: "[Helix] Custom Shield", // Custom rule name (visible in dashboard)
      waf_expression: 'ip.geoip.country eq "CN"', // Example: block China traffic (customize as needed)
      waf_action: "block",               // Options: block, challenge, managed_challenge, js_challenge
      
      // === PERFORMANCE (Optimized for speed) ===
      http3: "on",                       // HTTP/3 with QUIC (40% faster in poor networks, UDP-based)
      http2: "on",                       // HTTP/2 multiplexing (required for modern apps)
      zero_rtt: "on",                    // 0-RTT resumption (eliminates round-trip for repeat visitors)
      early_hints: "on",                 // 103 Early Hints (preload critical assets before HTML arrives)
      brotli: "on",                      // Brotli compression (20% better than gzip for text)
      
      // Caching
      caching_level: "Standard",         // Standard caching (respects origin cache headers)
      
      // === RELIABILITY ===
      always_online: "on",               // Serve cached pages if origin is down (99.99% uptime appearance)
      ipv6: "on",                        // IPv6 support (future-proof, required for some mobile networks)
      websockets: "on",                  // WebSocket proxy (required for real-time apps)
      
      // === CONTENT OPTIMIZATION ===
      email_obfuscation: "on",           // Hide emails from scrapers (anti-spam)
      // hotlink_protection: "off",      // DISABLED - Cloudflare API error 500 (can enable manually in dashboard)
      rocket_loader: "off",              // Async JS loading (DISABLED - can break some frameworks like React)
      mirage: "off",                     // Lazy load images (DISABLED - conflicts with modern frameworks)
      polish: "off",                     // Image optimization (requires Pro+ plan)
      
      // === DEVELOPMENT FRIENDLY ===
      development_mode: "off",           // Bypass cache for 3hrs (enable during active development only)
      
      // Network & Headers
      pseudo_ipv4: "off",                // IPv6 → IPv4 translation (rarely needed, keep off)
      ip_geolocation: "on",              // Add CF-IPCountry header (useful for geo-targeting in apps)
      true_client_ip_header: "on",       // Add True-Client-IP header (needed for accurate logging)
      
      // Advanced
      opportunistic_encryption: "on",    // Opportunistic TLS for HTTP requests (extra security layer)
      proxy_read_timeout: "100",         // 100s origin timeout (generous for slow APIs/reports)
      response_buffering: "off",         // Streaming responses (required for SSE/long-polling)
      sort_query_string_for_cache: "off", // Preserve query string order (safer for apps with signatures)
      prefetch_preload: "off",           // Disable prefetch (avoid extra origin load)
      server_side_exclude: "on",         // SSE tags support (<!--sse-->...<!--/sse-->)
      cname_flattening: "flatten_at_root" // CNAME flattening (required for root domain CNAME)
    }
  },
  {
    id: "high_performance",
    category: "se",
    name: "⚡ High Performance",
    description: "Optimized for speed. Ideal for React, Vue or static sites.",
    benefits: [
      "Reduces Time to First Byte (TTFB).",
      "Optimizes images automatically (WebP/Polish).",
      "Accelerates JavaScript loading with Rocket Loader.",
      "Ideal for high-traffic marketing sites."
    ],
    rules: {
      rocket_loader: "on",
      early_hints: "on",
      brotli: "on",
      mirage: "on",
      caching_level: "aggressive",
      http3: "on",
      zero_rtt: "on",
      polish: "lossless",
      webp: "on",
      waf_enable: "off"
    }
  },
  {
    id: "under_attack",
    category: "se",
    name: "🚨 Bunker Mode (DDoS)",
    description: "Emergency configuration. Activates maximum shields.",
    benefits: [
      "Mitigates Layer 7 HTTP Floods.",
      "Challenges every visitor (CAPTCHA/JS Challenge).",
      "Blocks non-standard browser signatures.",
      "Prevents hotlinking to save bandwidth."
    ],
    rules: {
      security_level: "under_attack",
      browser_check: "on",
      waf_enable: "on",
      waf_name: "[Helix] Bunker Mode Defense", // NOMBRE ESPECIFICO
      waf_action: "managed_challenge",
      waf_expression: "(not ip.geoip.country in {\"MX\" \"US\" \"ES\"})", // Example: Challenge everything except these countries
      waf_managed_owasp: "on",
      waf_managed_cloudflare: "on"
      // hotlink_protection: "on"  // DISABLED - Cloudflare API error 500
    }
  },
  {
    id: "bot_management_onboarding",
    category: "tase",
    name: "🤖 Bot Management Onboarding",
    description: "Enterprise Bot Management activation playbook (TASE). Progressive rules for definite bots, likely bots, JS detection, and AI bot control. Requires Bot Management module enabled on zone.",
    benefits: [
      "✅ Blocks definite bots (score = 1) — excludes verified bots & static assets",
      "✅ Managed Challenge for likely bots (score 2–29)",
      "✅ JS Challenge for clients failing JavaScript detection",
      "✅ Blocks AI crawlers (OpenAI, Anthropic, Perplexity, etc.)",
      "✅ ML model auto-updates enabled for continuous accuracy",
      "✅ All rules deployed in Log mode by default — switch to active after 24–48h validation"
    ],
    rules: {
      // === BOT MANAGEMENT CORE ===
      // Enable ML auto-updates: Security → Settings → Bot Management → Enable Auto-update ML model
      bot_ml_auto_update: "on",

      // === RULE 1: Definite Bots (score = 1) ===
      // Template: "Mitigate definite bot traffic"
      // Expression: (cf.bot_management.score eq 1 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)
      // Starts in LOG mode — switch to "block" after baseline validation (24–48h)
      bot_definite_enable: "on",
      bot_definite_name: "[Helix] Bot Management — Definite Bots",
      bot_definite_expression: "(cf.bot_management.score eq 1 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)",
      bot_definite_action: "log",            // Change to "block" after validation

      // === RULE 2: Likely Bots (score 2–29) ===
      // Template: "Mitigate likely bot traffic"
      // Expression: (cf.bot_management.score ge 2 and cf.bot_management.score le 29 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)
      // Starts in LOG mode — switch to "managed_challenge" after baseline validation
      bot_likely_enable: "on",
      bot_likely_name: "[Helix] Bot Management — Likely Bots",
      bot_likely_expression: "(cf.bot_management.score ge 2 and cf.bot_management.score le 29 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)",
      bot_likely_action: "log",              // Change to "managed_challenge" after validation

      // === RULE 3: JavaScript Detection (optional — requires JS Detections feature enabled) ===
      // Template: "Mitigate requests from clients who have not enabled JavaScript"
      // Enable via: Security → Settings → Bot Management → Enable Javascript Detections
      bot_js_detection_enable: "on",
      bot_js_detection_name: "[Helix] Bot Management — JS Detection",
      bot_js_detection_expression: '(not cf.bot_management.js_detection.passed and http.request.method eq "GET" and not cf.bot_management.verified_bot)',
      bot_js_detection_action: "log",        // Change to "js_challenge" after validation

      // === RULE 4: AI Bot Block (optional) ===
      // Blocks verified AI crawlers (OpenAI, Anthropic, Perplexity, etc.)
      bot_ai_block_enable: "on",
      bot_ai_block_name: "[Helix] Bot Management — AI Bots",
      // Detection IDs from https://developers.cloudflare.com/ai-crawl-control/reference/bots/
      // Covers GPTBot, ChatGPT-User, OAI-SearchBot, ClaudeBot (all variants),
      // PerplexityBot, Bytespider, CCBot, Meta bots, Amazonbot, Google-CloudVertexBot,
      // DuckAssistBot, MistralAI-User
      bot_ai_block_expression: "(any(cf.bot_management.detection_ids[*] in {123815556 132995013 126255384 33563859 33564301 33564303 33563889 33564371 33563853 133621792 33563855 124581738 33563982 33563972 118601807 133730073 126666910 128950951}))",
      bot_ai_block_action: "log"             // Change to "block" or "managed_challenge" after review
    }
  },
  {
    id: "spectrum_implementation",
    category: "tase",
    name: "📡 Spectrum Implementation",
    description: "Enterprise Cloudflare Spectrum activation playbook (TASE). Proxies non-HTTP protocols (SSH, RDP, SFTP, custom TCP/UDP) through Cloudflare with L3/L4 DDoS protection, TLS termination, IP Firewall, and optional Argo Smart Routing. Requires Spectrum entitlement enabled on the account.",
    benefits: [
      "✅ L3/L4 DDoS protection for TCP/UDP applications",
      "✅ Hides origin IP — all traffic routes through Cloudflare edge",
      "✅ TLS termination at edge (full mode by default)",
      "✅ IP Firewall enforcement via Access Rules",
      "✅ Optional Argo Smart Routing for lowest-latency path",
      "✅ HTTP → HTTPS redirect via always_use_https",
      "✅ DNS record auto-created by Cloudflare — no manual DNS entry needed",
      "✅ Supports SSH, RDP, SFTP, SMTP, custom TCP/UDP ports"
    ],
    rules: {
      // === SPECTRUM APP 1: HTTPS (port 443) ===
      // Primary app — handles HTTPS traffic through Cloudflare L4 proxy
      spectrum_app1_enable: "on",
      spectrum_app1_protocol: "tcp/443",
      spectrum_app1_traffic_type: "direct",
      spectrum_app1_dns_name: "",                    // e.g. myapp.example.com — filled by operator
      spectrum_app1_origin_type: "dns",              // "dns" or "direct"
      spectrum_app1_origin_dns_name: "",             // e.g. origin.example.com
      spectrum_app1_origin_port: "443",
      spectrum_app1_tls: "full",
      spectrum_app1_ip_firewall: "on",
      spectrum_app1_proxy_protocol: "off",
      spectrum_app1_argo: "on",
      spectrum_app1_edge_connectivity: "all",

      // === SPECTRUM APP 2: HTTP (port 80) ===
      // Required companion app — without this, HTTP traffic returns an error.
      // Combined with always_use_https to redirect HTTP → HTTPS at edge.
      spectrum_app2_enable: "on",
      spectrum_app2_protocol: "tcp/80",
      spectrum_app2_traffic_type: "direct",
      spectrum_app2_dns_name: "",                    // Same hostname as App 1
      spectrum_app2_origin_type: "dns",
      spectrum_app2_origin_dns_name: "",             // Same origin as App 1
      spectrum_app2_origin_port: "80",
      spectrum_app2_tls: "off",
      spectrum_app2_ip_firewall: "on",
      spectrum_app2_proxy_protocol: "off",
      spectrum_app2_argo: "off",
      spectrum_app2_edge_connectivity: "all",

      // === HTTP → HTTPS REDIRECT ===
      // Forces all HTTP traffic to HTTPS at the Cloudflare edge
      always_use_https: "on"
    }
  },
  {
    id: "spectrum_ssh",
    category: "tase",
    name: "🔐 Spectrum SSH / RDP",
    description: "Spectrum implementation for SSH (port 22) or RDP (port 3389). Proxies the protocol through Cloudflare L4 with DDoS protection and Argo Smart Routing. Requires Spectrum entitlement.",
    benefits: [
      "✅ Hides SSH/RDP origin IP behind Cloudflare anycast",
      "✅ L3/L4 DDoS protection on SSH/RDP port",
      "✅ Argo Smart Routing for lowest-latency connection",
      "✅ IP Firewall via Access Rules",
      "✅ Proxy Protocol v2 passes real client IP to origin"
    ],
    rules: {
      spectrum_app1_enable: "on",
      spectrum_app1_protocol: "tcp/22",              // Change to tcp/3389 for RDP
      spectrum_app1_traffic_type: "direct",
      spectrum_app1_dns_name: "",                    // e.g. ssh.example.com
      spectrum_app1_origin_type: "dns",
      spectrum_app1_origin_dns_name: "",             // e.g. origin.example.com
      spectrum_app1_origin_port: "22",               // Change to 3389 for RDP
      spectrum_app1_tls: "off",                      // SSH handles its own encryption
      spectrum_app1_ip_firewall: "on",
      spectrum_app1_proxy_protocol: "v2",            // Pass real client IP to origin
      spectrum_app1_argo: "on",
      spectrum_app1_edge_connectivity: "all",
      spectrum_app2_enable: "off"
    }
  },
  {
    id: "wordpress",
    category: "se",
    name: "📝 WordPress Secure",
    description: "Specific hardening for WP. Protects logins and XML-RPC.",
    benefits: [
      "Blocks XML-RPC attacks (common brute force vector).",
      "Protects /wp-admin login pages.",
      "Fixes mixed content errors automatically.",
      "Hardens security against bot scrapers."
    ],
    rules: {
      security_level: "high",
      automatic_https_rewrites: "on",
      waf_enable: "on",
      waf_name: "[Helix] WordPress Hardening", // NOMBRE ESPECIFICO
      waf_expression: '(http.request.uri.path contains "/wp-login.php") or (http.request.uri.path contains "/xmlrpc.php")',
      waf_action: "managed_challenge",
      // hotlink_protection: "on",  // DISABLED - Cloudflare API error 500
      email_obfuscation: "on"
    }
  }
];
